
import React from "react";
import TestTypeDetector from "./scoring/TestTypeDetector";
import ScoreDisplay from "./scoring/ScoreDisplay";
import ScoringButtonsSelector from "./scoring/ScoringButtonsSelector";
import CancelButton from "./scoring/CancelButton";
import { getCorrectDistance } from "./scoring/utils/testTypeHelpers";

interface ScoringPanelProps {
  testId: string | null;
  currentPoint: string;
  currentAttempt: number;
  maxAttempts: number;
  distance: string;
  onScoreChange: (score: number, tendency?: any) => void;
  onCancelLastEntry: () => void;
  isChippingTest?: boolean;
  isWedgingTest?: boolean;
  isBunkerTest?: boolean;
}

const ScoringPanel: React.FC<ScoringPanelProps> = ({
  testId,
  currentPoint,
  currentAttempt,
  maxAttempts,
  distance,
  onScoreChange,
  onCancelLastEntry,
  isChippingTest: propIsChippingTest,
  isWedgingTest: propIsWedgingTest,
  isBunkerTest: propIsBunkerTest
}) => {
  console.log("Level2 ScoringPanel - testId:", testId);
  console.log("Level2 ScoringPanel - isChipping:", propIsChippingTest);
  console.log("Level2 ScoringPanel - isWedging:", propIsWedgingTest);
  console.log("Level2 ScoringPanel - currentPoint:", currentPoint);
  console.log("Level2 ScoringPanel - distance:", distance);

  // Vérifier si nous sommes dans un test bunker spécifique
  const isBunkerCourt = testId === "bunker-court";
  const isBunkerLong = testId === "bunker-long";
  
  // Vérifier si nous sommes dans un test iron spécifique
  const isIronTest = testId?.includes('iron');
  const isFairwayWoodTest = testId?.includes('fairwayWood');
  const isDriverTest = testId?.includes('driver');

  // Obtenir la bonne distance pour les tests spécifiques du niveau 2
  const getLevel2Distance = () => {
    // Pour les tests de fer
    if (isIronTest) {
      if (testId === 'iron-long') return '130m';
      if (testId === 'iron-mid') return '80m';
    }
    
    // Pour les tests de bois de parcours
    if (isFairwayWoodTest) {
      if (testId === 'fairwayWood-3') return '180m';
      if (testId === 'fairwayWood-5') return '160m';
    }
    
    // Pour les tests de driver
    if (isDriverTest) return '180m';
    
    // Pour les tests de bunker
    if (isBunkerCourt) {
      if (currentPoint === "BunkerA" || currentPoint === "D") return "5m";
      if (currentPoint === "BunkerB" || currentPoint === "E") return "8m";
      if (currentPoint === "BunkerC" || currentPoint === "F") return "12m";
    }
    
    if (isBunkerLong) {
      if (currentPoint === "BunkerD" || currentPoint === "D1") return "15m";
      if (currentPoint === "E1") return "20m";
    }

    return distance;
  };

  return (
    <TestTypeDetector 
      testId={testId}
      propIsChippingTest={propIsChippingTest}
      propIsWedgingTest={propIsWedgingTest}
      propIsBunkerTest={propIsBunkerTest}
    >
      {({ isPutting, isChipping, isWedging, isBunker, isIron, isFairwayWood, isDriver }) => {
        // Récupération du premier point pour ce test
        const isLevel2 = window.location.pathname.includes('level2');
        let correctDistance = distance;
        
        // Priorité aux tests spécifiques du niveau 2
        if (isLevel2) {
          correctDistance = getLevel2Distance();
        } else {
          // Sinon, utiliser la fonction standard
          correctDistance = getCorrectDistance(testId, currentPoint, distance);
        }
        
        // Calcul du premier point (pour déterminer si le bouton d'annulation doit être désactivé)
        let firstPoint = "";
        if (isLevel2) {
          if (isPutting) firstPoint = "D";
          else if (isChipping && testId === "chipping-plat") firstPoint = "D";
          else if (isChipping && testId === "chipping-pente-droit") firstPoint = "D1";
          else if (isChipping && testId === "chipping-pente-gauche") firstPoint = "D2";
          else if (isChipping && testId === "chipping-roulee-plat") firstPoint = "D3";
          else if (isWedging && testId === "wedging-cercles") firstPoint = "D";
          else if (isWedging && testId === "wedging-mid") firstPoint = "D1";
          else if (isWedging && testId === "wedging-pente-gauche") firstPoint = "D2";
          else if (isBunker && testId === "bunker-court") firstPoint = "D";
          else if (isBunker && testId === "bunker-long") firstPoint = "D1";
          else if (isIron || isFairwayWood || isDriver) firstPoint = "D";
          else firstPoint = "D";
        } else {
          // Utiliser la logique de niveau 3
          if (isPutting) firstPoint = "A";
          else if (isChipping) firstPoint = "TerPlatA";
          else if (isWedging) firstPoint = "WedgingA";
          else if (isBunker) firstPoint = "BunkerA";
          else if (isIron) firstPoint = "IronA";
          else if (isFairwayWood) firstPoint = "FairwayWoodA";
          else firstPoint = "D";
        }
        
        const cancelButtonDisabled = currentPoint === firstPoint && currentAttempt === 0;

        return (
          <div className="bg-white/95 rounded-md shadow-sm p-4 border border-gray-200">
            <ScoreDisplay 
              currentPoint={currentPoint}
              currentAttempt={currentAttempt}
              maxAttempts={maxAttempts}
              correctDistance={correctDistance}
              isPutting={isPutting}
              isChipping={isChipping}
              testId={testId}
            />

            <div className="grid grid-cols-1 gap-2 mb-4">
              <ScoringButtonsSelector 
                isPutting={isPutting}
                isChipping={isChipping}
                isWedging={isWedging}
                isBunker={isBunker}
                isIron={isIron}
                isFairwayWood={isFairwayWood}
                isDriver={isDriver}
                testId={testId}
                onScoreChange={onScoreChange}
              />
              
              <CancelButton 
                onCancelLastEntry={onCancelLastEntry}
                disabled={cancelButtonDisabled} 
              />
            </div>
          </div>
        );
      }}
    </TestTypeDetector>
  );
};

export default ScoringPanel;
